import React, { useEffect, useState } from 'react';
import { adminAPI, apiKeysAPI, sponsorsAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import DashboardOverview from './admin/DashboardOverview';
import UserDetailModal from './admin/UserDetailModal';
import AdminNoteModal from './admin/AdminNoteModal';

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'verification' | 'keys' | 'reports' | 'sponsors'>('overview');
  const [sponsors, setSponsors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [queue, setQueue] = useState<any[]>([]);
  const [keys, setKeys] = useState<any[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [success, setSuccess] = useState('');
  // users tab controls
  const [userPage, setUserPage] = useState(1);
  const [userLimit, setUserLimit] = useState(20);
  const [userVerified, setUserVerified] = useState<'all' | 'true' | 'false'>('all');
  const [userSearch, setUserSearch] = useState('');
  const [userSort, setUserSort] = useState<'created' | 'name' | 'email'>('created');
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [showUserDetail, setShowUserDetail] = useState<number | null>(null);
  // 큐 승인/반려 메모 입력 모달 (window.prompt 대체)
  const [queueNoteModal, setQueueNoteModal] = useState<null | { action: 'verify' | 'reject'; userId: number }>(null);
  const [queueActionLoading, setQueueActionLoading] = useState(false);
  // reports tab controls
  const [reportPage, setReportPage] = useState(1);
  const [reportLimit, setReportLimit] = useState(20);
  const [reportStatus, setReportStatus] = useState<'pending' | 'resolved' | 'dismissed'>('pending');
  const [reportTotalPages, setReportTotalPages] = useState(1);
  // keys usage
  const [usage, setUsage] = useState<any[]>([]);
  const [usageDays, setUsageDays] = useState(7);

  // 관리자 권한 체크 — is_admin 클레임 우선(Phase 0 역할 일반화), 구 계정 이메일은 폴백
  const isAdmin =
    (user as any)?.isAdmin === true ||
    user?.email === 'admin@pulse.com' ||
    user?.email === 'admin.test@pulse.com';

  // 계정 유형 전환
  const ACCOUNT_TYPES: Array<{ value: 'clinician' | 'pharma_pro' | 'recruiter' | 'society'; label: string }> = [
    { value: 'clinician', label: '의료진' },
    { value: 'pharma_pro', label: '제약 전문가' },
    { value: 'recruiter', label: '리크루터' },
    { value: 'society', label: '학회' },
  ];
  const handleAccountTypeChange = async (userId: number, next: string) => {
    if (!next) return;
    if (!window.confirm(`이 계정의 유형을 '${ACCOUNT_TYPES.find(t => t.value === next)?.label ?? next}'(으)로 변경할까요?\n(해당 사용자는 재로그인 후 반영됩니다)`)) return;
    try {
      await adminAPI.setAccountType(userId, next as any);
      setUsers(prev => prev.map(u => (u.id === userId ? { ...u, account_type: next, accountType: next } : u)));
      setSuccess('계정 유형이 변경되었습니다. 해당 사용자는 재로그인 후 반영됩니다.');
    } catch (e: any) {
      setError(e?.response?.data?.error || '계정 유형 변경에 실패했습니다.');
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      setError('관리자 권한이 필요합니다.');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError('');
      setSuccess('');
      try {
        if (activeTab === 'users') {
          const verifiedParam = userVerified === 'all' ? undefined : userVerified === 'true';
          const res = await adminAPI.getUsers({ page: userPage, limit: userLimit, verified: verifiedParam as any });
          let list = res.data.users || [];
          // client-side search & sort
          if (userSearch) {
            const q = userSearch.toLowerCase();
            list = list.filter((u:any) => `${u.email} ${u.name}`.toLowerCase().includes(q));
          }
          if (userSort === 'name') list.sort((a:any,b:any)=> (a.name||'').localeCompare(b.name||''));
          else if (userSort === 'email') list.sort((a:any,b:any)=> (a.email||'').localeCompare(b.email||''));
          // created: keep backend order (DESC)
          setUsers(list);
          setUserTotalPages(res.data.pagination?.totalPages || 1);
        } else if (activeTab === 'verification') {
          const res = await adminAPI.getVerificationQueue({ page: 1, limit: 50 });
          setQueue(res.data.queue || []);
        } else if (activeTab === 'keys') {
          const res = await apiKeysAPI.getKeys();
          setKeys(res.data.keys || []);
          const u = await apiKeysAPI.getUsage(usageDays);
          setUsage(u.data.usage || []);
        } else if (activeTab === 'sponsors') {
          const res = await sponsorsAPI.adminList();
          setSponsors(res.data.sponsors || []);
        } else if (activeTab === 'reports') {
          const res = await adminAPI.getReports({ page: reportPage, limit: reportLimit, status: reportStatus });
          setReports(res.data.reports || []);
          setReportTotalPages(res.data.pagination?.totalPages || 1);
        }
      } catch (e: any) {
        setError(e?.response?.data?.error || '데이터를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeTab, isAdmin, userPage, userLimit, userVerified, userSearch, userSort, usageDays, reportPage, reportLimit, reportStatus]);

  const handleUserAction = async (action: 'verify' | 'unverify', userId: number) => {
    try {
      if (action === 'verify') {
        await adminAPI.verifyUser(userId);
        setSuccess('사용자가 검증되었습니다.');
      } else {
        await adminAPI.unverifyUser(userId);
        setSuccess('사용자 검증이 철회되었습니다.');
      }
      // 데이터 새로고침
      const res = await adminAPI.getUsers({});
      setUsers(res.data.users || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || '작업을 수행할 수 없습니다.');
    }
  };

  const refreshQueue = async () => {
    const res = await adminAPI.getVerificationQueue({ page: 1, limit: 50 });
    setQueue(res.data.queue || []);
  };

  // 큐 승인/반려 버튼 → 메모 입력 모달 오픈 (window.prompt 대체)
  const handleQueueDecision = (action: 'verify' | 'reject', userId: number) => {
    setQueueNoteModal({ action, userId });
  };

  // 메모 모달 확인 → 실제 승인/반려 API 호출
  const submitQueueNote = async (note: string) => {
    if (!queueNoteModal) return;
    const { action, userId } = queueNoteModal;
    try {
      setQueueActionLoading(true);
      if (action === 'verify') {
        await adminAPI.verifyUser(userId, note || undefined);
        setSuccess('면허 검증이 승인되었습니다.');
      } else {
        await adminAPI.rejectUser(userId, note || undefined);
        setSuccess('면허 검증이 반려되었습니다.');
      }
      setQueueNoteModal(null);
      await refreshQueue();
    } catch (e: any) {
      setError(e?.response?.data?.error || '작업을 수행할 수 없습니다.');
    } finally {
      setQueueActionLoading(false);
    }
  };

  const handleBulkAction = async (action: 'verify' | 'unverify') => {
    if (selectedUsers.length === 0) {
      alert('사용자를 선택해주세요.');
      return;
    }

    const actionText = action === 'verify' ? '검증' : '검증 철회';
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`선택한 ${selectedUsers.length}명의 사용자를 ${actionText} 하시겠습니까?`)) {
      return;
    }

    try {
      setLoading(true);
      await adminAPI.bulkUserAction(selectedUsers, action);
      setSuccess(`${selectedUsers.length}명의 사용자가 ${actionText}되었습니다.`);
      setSelectedUsers([]);
      
      // 데이터 새로고침
      const res = await adminAPI.getUsers({ page: userPage, limit: userLimit });
      setUsers(res.data.users || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || '일괄 작업을 수행할 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const handleKeyRevoke = async (keyId: number) => {
    try {
      await apiKeysAPI.revokeKey(keyId);
      setSuccess('API 키가 철회되었습니다.');
      // 데이터 새로고침
      const res = await apiKeysAPI.getKeys();
      setKeys(res.data.keys || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'API 키 철회에 실패했습니다.');
    }
  };

  const handleReportAction = async (reportId: number, action: 'resolved' | 'dismissed') => {
    try {
      await adminAPI.resolveReport(reportId, action);
      setSuccess(`신고가 ${action === 'resolved' ? '해결' : '기각'}되었습니다.`);
      // 데이터 새로고침
      const res = await adminAPI.getReports({});
      setReports(res.data.reports || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || '신고 처리에 실패했습니다.');
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">관리자 대시보드</h1>
        <div className="text-red-600 bg-red-50 p-4 rounded-lg">
          <h2 className="font-semibold mb-2">접근 권한 없음</h2>
          <p>이 페이지는 관리자만 접근할 수 있습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">관리자 대시보드</h1>
      <div className="flex space-x-2 mb-4">
        <button onClick={() => setActiveTab('overview')} className={`px-3 py-1 rounded ${activeTab==='overview'?'bg-gray-900 text-white':'bg-gray-100 text-gray-600'}`}>개요</button>
        <button onClick={() => setActiveTab('users')} className={`px-3 py-1 rounded ${activeTab==='users'?'bg-gray-900 text-white':'bg-gray-100 text-gray-600'}`}>사용자 검증</button>
        <button onClick={() => setActiveTab('verification')} className={`px-3 py-1 rounded ${activeTab==='verification'?'bg-gray-900 text-white':'bg-gray-100 text-gray-600'}`}>면허 검증 큐</button>
        <button onClick={() => setActiveTab('keys')} className={`px-3 py-1 rounded ${activeTab==='keys'?'bg-gray-900 text-white':'bg-gray-100 text-gray-600'}`}>API Key</button>
        <button onClick={() => setActiveTab('reports')} className={`px-3 py-1 rounded ${activeTab==='reports'?'bg-gray-900 text-white':'bg-gray-100 text-gray-600'}`}>신고 목록</button>
        <button onClick={() => setActiveTab('sponsors')} className={`px-3 py-1 rounded ${activeTab==='sponsors'?'bg-gray-900 text-white':'bg-gray-100 text-gray-600'}`}>스폰서</button>
      </div>
      {error && <div className="text-red-600 bg-red-50 p-3 rounded mb-3">{error}</div>}
      {success && <div className="text-green-600 bg-green-50 p-3 rounded mb-3">{success}</div>}
      {loading ? (
        <div>불러오는 중...</div>
      ) : activeTab === 'overview' ? (
        <DashboardOverview />
      ) : activeTab === 'users' ? (
        <>
        {/* 일괄 작업 액션 바 */}
        {selectedUsers.length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 flex items-center justify-between">
            <span className="text-gray-900 font-medium">
              {selectedUsers.length}명 선택됨
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => handleBulkAction('verify')}
                className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium"
              >
                선택 검증
              </button>
              <button
                onClick={() => handleBulkAction('unverify')}
                className="px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm font-medium"
              >
                선택 철회
              </button>
              <button
                onClick={() => setSelectedUsers([])}
                className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm font-medium"
              >
                선택 해제
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <input value={userSearch} onChange={(e)=>setUserSearch(e.target.value)} placeholder="이메일/이름 검색" className="px-2 py-1 border rounded" />
            <select value={userVerified} onChange={(e)=>setUserVerified(e.target.value as any)} className="px-2 py-1 border rounded">
              <option value="all">전체</option>
              <option value="true">검증됨</option>
              <option value="false">대기</option>
            </select>
            <select value={userSort} onChange={(e)=>setUserSort(e.target.value as any)} className="px-2 py-1 border rounded">
              <option value="created">생성일</option>
              <option value="name">이름</option>
              <option value="email">이메일</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <select value={userLimit} onChange={(e)=>{setUserLimit(Number(e.target.value)); setUserPage(1);}} className="px-2 py-1 border rounded">
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <div className="space-x-2">
              <button disabled={userPage<=1} onClick={()=>setUserPage(p=>p-1)} className="px-2 py-1 border rounded disabled:opacity-50">이전</button>
              <span className="text-sm">{userPage} / {userTotalPages}</span>
              <button disabled={userPage>=userTotalPages} onClick={()=>setUserPage(p=>p+1)} className="px-2 py-1 border rounded disabled:opacity-50">다음</button>
            </div>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b bg-gray-50">
              <th className="py-2 px-2">
                <input
                  type="checkbox"
                  checked={selectedUsers.length === users.length && users.length > 0}
                  onChange={toggleAllUsers}
                  className="w-4 h-4"
                />
              </th>
              <th className="py-2">이메일</th>
              <th>이름</th>
              <th>계정 유형</th>
              <th>검증여부</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b hover:bg-gray-50">
                <td className="py-2 px-2">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(u.id)}
                    onChange={() => toggleUserSelection(u.id)}
                    className="w-4 h-4"
                  />
                </td>
                <td className="py-2">{u.email}</td>
                <td>{u.name}</td>
                <td>
                  <select
                    value={(u.account_type ?? u.accountType) || 'clinician'}
                    onChange={(e) => handleAccountTypeChange(u.id, e.target.value)}
                    disabled={(u.account_type ?? u.accountType) === 'admin'}
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:bg-gray-50 disabled:text-gray-400"
                    title={(u.account_type ?? u.accountType) === 'admin' ? '관리자 계정 유형은 이 화면에서 변경할 수 없습니다' : '계정 유형 전환'}
                  >
                    {(u.account_type ?? u.accountType) === 'admin' && <option value="admin">관리자</option>}
                    {ACCOUNT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <span className={`px-2 py-1 rounded-full text-xs border ${
                    u.isVerifiedDoctor ? 'border-gray-900 text-gray-900' : 'border-gray-200 text-gray-500'
                  }`}>
                    {u.isVerifiedDoctor ? '검증됨' : '대기'}
                  </span>
                </td>
                <td className="space-x-2">
                  <button
                    onClick={() => setShowUserDetail(u.id)}
                    className="px-2 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs"
                  >
                    상세
                  </button>
                  {!u.isVerifiedDoctor ? (
                    <button onClick={() => handleUserAction('verify', u.id)} className="px-2 py-1 bg-gray-900 text-white rounded hover:bg-gray-700 text-xs">승인</button>
                  ) : (
                    <button onClick={() => handleUserAction('unverify', u.id)} className="px-2 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs">철회</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* 사용자 상세 모달 */}
        {showUserDetail && (
          <UserDetailModal
            userId={showUserDetail}
            onClose={() => setShowUserDetail(null)}
            onAction={() => {
              // 데이터 새로고침
              const fetchData = async () => {
                const res = await adminAPI.getUsers({ page: userPage, limit: userLimit });
                setUsers(res.data.users || []);
              };
              fetchData();
            }}
          />
        )}
        </>
      ) : activeTab === 'verification' ? (
        <>
        <div className="font-semibold mb-3">면허 검증 대기열 ({queue.length})</div>
        {queue.length === 0 ? (
          <div className="text-gray-600">대기 중인 검증 요청이 없습니다.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2">이름</th>
                <th>이메일</th>
                <th>면허번호</th>
                <th>방식</th>
                <th>점수</th>
                <th>이미지</th>
                <th>액션</th>
              </tr>
            </thead>
            <tbody>
              {queue.map((q:any) => (
                <tr key={q.id} className="border-b align-top">
                  <td className="py-2">{q.name}</td>
                  <td>{q.email}</td>
                  <td>{q.doctorLicenseNumber || q.specialistLicenseNumber || '-'}</td>
                  <td>{q.verificationMethod || '-'}</td>
                  <td>{q.verificationScore ?? '-'}</td>
                  <td>
                    {q.doctorLicenseUrl ? (
                      <a href={q.doctorLicenseUrl} target="_blank" rel="noreferrer" className="text-gray-900 underline">의사</a>
                    ) : null}
                    {q.specialistLicenseUrl ? (
                      <a href={q.specialistLicenseUrl} target="_blank" rel="noreferrer" className="text-gray-900 underline ml-2">전문의</a>
                    ) : null}
                    {!q.doctorLicenseUrl && !q.specialistLicenseUrl ? '-' : null}
                  </td>
                  <td className="space-x-2 whitespace-nowrap">
                    <button onClick={() => setShowUserDetail(q.id)} className="px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700">상세</button>
                    <button onClick={() => handleQueueDecision('verify', q.id)} className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700">승인</button>
                    <button onClick={() => handleQueueDecision('reject', q.id)} className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700">반려</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </>
      ) : activeTab === 'sponsors' ? (
        sponsors.length === 0 ? (
          <div className="text-gray-600">등록된 스폰서가 없습니다.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b bg-gray-50">
                <th className="py-2">회사명</th><th>담당자</th><th>사업자번호</th><th>상태</th><th>액션</th>
              </tr>
            </thead>
            <tbody>
              {sponsors.map((s) => {
                const st = s.status;
                return (
                <tr key={s.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 font-medium">{s.company_name ?? s.companyName}</td>
                  <td className="text-gray-500">{s.owner_email ?? s.ownerEmail}</td>
                  <td className="text-gray-500">{s.business_registration ?? s.businessRegistration ?? '—'}</td>
                  <td>
                    <span className={`px-2 py-1 rounded-full text-xs border ${
                      st === 'approved' ? 'border-gray-900 text-gray-900' : st === 'suspended' ? 'border-red-300 text-red-600' : 'border-gray-200 text-gray-500'
                    }`}>
                      {st === 'approved' ? '승인됨' : st === 'suspended' ? '정지' : '대기'}
                    </span>
                  </td>
                  <td className="space-x-2">
                    {st !== 'approved' && (
                      <button
                        onClick={async () => { try { await sponsorsAPI.adminApprove(s.id); setSponsors(p => p.map(x => x.id === s.id ? { ...x, status: 'approved' } : x)); setSuccess('스폰서가 승인되었습니다.'); } catch (e: any) { setError(e?.response?.data?.error || '승인 실패'); } }}
                        className="px-2 py-1 bg-gray-900 text-white rounded hover:bg-gray-700 text-xs"
                      >승인</button>
                    )}
                    {st !== 'suspended' && (
                      <button
                        onClick={async () => { if (!window.confirm('이 스폰서를 정지할까요? 광고 송출이 중단됩니다.')) return; try { await sponsorsAPI.adminSuspend(s.id); setSponsors(p => p.map(x => x.id === s.id ? { ...x, status: 'suspended' } : x)); setSuccess('스폰서가 정지되었습니다.'); } catch (e: any) { setError(e?.response?.data?.error || '정지 실패'); } }}
                        className="px-2 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs"
                      >정지</button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )
      ) : activeTab === 'keys' ? (
        keys.length === 0 ? (
          <div className="text-gray-600">발급된 키가 없습니다.</div>
        ) : (
          <>
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold">API 키 목록</div>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">사용량 기간</label>
              <select value={usageDays} onChange={(e)=>setUsageDays(Number(e.target.value))} className="px-2 py-1 border rounded">
                <option value={7}>7일</option>
                <option value={14}>14일</option>
                <option value={30}>30일</option>
              </select>
            </div>
          </div>
          <table className="w-full text-sm mb-6">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2">ID</th>
                <th>상태</th>
                <th>쿼터/일</th>
                <th>액션</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k:any) => (
                <tr key={k.id} className="border-b">
                  <td className="py-2">{k.id}</td>
                  <td>{k.status}</td>
                  <td>{k.quotaPerDay}</td>
                  <td>
                    {k.status==='active' && (
                      <button onClick={() => handleKeyRevoke(k.id)} className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700">철회</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="font-semibold mb-2">최근 사용량</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2">키 ID</th>
                <th>날짜</th>
                <th>요청수</th>
                <th>엔드포인트 수</th>
              </tr>
            </thead>
            <tbody>
              {usage.map((u:any, idx:number)=> (
                <tr key={idx} className="border-b">
                  <td className="py-2">{u.api_key_id}</td>
                  <td>{u.usage_date}</td>
                  <td>{u.daily_usage}</td>
                  <td>{u.endpoint_count}</td>
                </tr>
              ))}
              {usage.length===0 && (
                <tr><td className="py-3 text-gray-500" colSpan={4}>사용량 데이터가 없습니다.</td></tr>
              )}
            </tbody>
          </table>
          </>
        )
      ) : (
        <>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <select value={reportStatus} onChange={(e)=>{setReportStatus(e.target.value as any); setReportPage(1);}} className="px-2 py-1 border rounded">
              <option value="pending">대기</option>
              <option value="resolved">해결</option>
              <option value="dismissed">기각</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <select value={reportLimit} onChange={(e)=>{setReportLimit(Number(e.target.value)); setReportPage(1);}} className="px-2 py-1 border rounded">
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <div className="space-x-2">
              <button disabled={reportPage<=1} onClick={()=>setReportPage(p=>p-1)} className="px-2 py-1 border rounded disabled:opacity-50">이전</button>
              <span className="text-sm">{reportPage} / {reportTotalPages}</span>
              <button disabled={reportPage>=reportTotalPages} onClick={()=>setReportPage(p=>p+1)} className="px-2 py-1 border rounded disabled:opacity-50">다음</button>
            </div>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b">
              <th className="py-2">ID</th>
              <th>사유</th>
              <th>상태</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r:any) => (
              <tr key={r.id} className="border-b">
                <td className="py-2">{r.id}</td>
                <td>{r.reason}</td>
                <td>{r.status}</td>
                <td className="space-x-2">
                  <button onClick={() => handleReportAction(r.id, 'resolved')} className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700">해결</button>
                  <button onClick={() => handleReportAction(r.id, 'dismissed')} className="px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700">기각</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}

      {/* 큐 승인/반려 메모 입력 모달 (window.prompt 대체) */}
      <AdminNoteModal
        open={queueNoteModal?.action === 'verify'}
        title="면허 검증 승인"
        description="승인 메모를 남기면 감사 로그에 기록됩니다. 예: 면허 사본 확인 완료"
        placeholder="승인 메모 (선택)"
        confirmLabel="승인"
        confirmTone="primary"
        loading={queueActionLoading}
        onConfirm={submitQueueNote}
        onCancel={() => setQueueNoteModal(null)}
      />
      <AdminNoteModal
        open={queueNoteModal?.action === 'reject'}
        title="면허 검증 반려"
        description="반려 사유를 입력하세요. 감사 로그에 기록됩니다. 예: 면허번호 불일치"
        placeholder="반려 사유"
        confirmLabel="반려"
        confirmTone="danger"
        requireNote
        loading={queueActionLoading}
        onConfirm={submitQueueNote}
        onCancel={() => setQueueNoteModal(null)}
      />
    </div>
  );
};

export default AdminDashboard;
